import { useCallback, useEffect, useState } from "react";
import type { Locale, StaffMember, StaffRosterResponse } from "../../shared/dto";
import type { StaffRun } from "./model";
import { translateCount } from "../i18n";
import { intlLanguage } from "../date-format";
import { isTestTenant } from "../../shared/test-tenant";
import { TestFilterNote } from "./TestFilterNote";
import "./unassigned-clients.css";

// F139 (Pavel 2026-08-12): "bude potreba, aby administrator mel moznost videt
// nove klienty, kteri jeste nemaji prirazene partaky. Videl idealne i jejich
// profily... mohl posoudit podle toho profilu a podle produktu, ktery maji
// koupeny, komu to priradi."
//
// So this is not a list of names with a button. Every row carries the four
// facts the decision is made from - what they bought, which market, when they
// arrived, how much of a profile we already hold - and the assignment itself,
// without leaving Dnes. It sits at the TOP of the admin's day because a client
// with no partner is the one thing on this screen nobody else can pick up.

export interface UnassignedClient {
  clientId: string;
  name: string;
  email: string;
  marketLanguage: Locale;
  createdAt: string;
  isTest: boolean;
  source: "stripe" | "manual";
  product: string | null;
  profileFacts: number;
  deepLink: string;
}

interface UnassignedResponse {
  ok: true;
  /** Every unassigned client, not only the page. A heading may not lie. */
  total: number;
  clients: UnassignedClient[];
}

/** The sentence a row prints about what the client bought. */
export function unassignedProductKey(client: Pick<UnassignedClient, "source" | "product">): string | null {
  if (client.product) return null;
  return client.source === "stripe"
    ? "staffConsole.unassigned.productUnknown"
    : "staffConsole.unassigned.productManual";
}

/**
 * A refused assignment names WHICH reality it hit. `partner_unavailable` is
 * the one an admin can act on without reloading: the member was deactivated
 * while the list sat on screen, so the move is to pick somebody else.
 */
export function assignErrorKey(code: string | null): string {
  return code === "partner_unavailable"
    ? "staffConsole.unassigned.errorPartnerUnavailable"
    : "staffConsole.unassigned.error";
}

export function StaffUnassignedClients(props: {
  run: StaffRun;
  t: (key: string) => string;
  locale: Locale;
  hideTestTenants: boolean;
  onOpenClient: (clientId: string, step: string | null) => void;
  onHideTestTenantsChange?: (hide: boolean) => void;
  /** Fixtures render the section without a server. */
  initialClients?: UnassignedClient[];
  initialPartners?: StaffMember[];
  timeZone?: string;
}) {
  const [clients, setClients] = useState<UnassignedClient[]>(props.initialClients ?? []);
  const [total, setTotal] = useState<number>(props.initialClients?.length ?? 0);
  const [partners, setPartners] = useState<StaffMember[]>(props.initialPartners ?? []);
  const [loaded, setLoaded] = useState(Boolean(props.initialClients));
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<Record<string, string>>({});
  const timeZone = props.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { run } = props;

  const load = useCallback(async () => {
    try {
      const [queue, roster] = await Promise.all([
        fetch("/api/admin/unassigned", { credentials: "include" }),
        fetch("/api/admin/staff", { credentials: "include" })
      ]);
      if (queue.ok) {
        const body = await queue.json() as UnassignedResponse;
        setClients(body.clients);
        setTotal(body.total ?? body.clients.length);
      }
      if (roster.ok) {
        const body = await roster.json() as StaffRosterResponse;
        setPartners(body.members.filter((member) => member.isActive));
      }
    } catch {
      // A failed load leaves the rows that are already on screen (the queue's
      // own N07 law); an empty first load simply renders nothing, because an
      // empty unassigned queue is the normal, good state.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (props.initialClients) return;
    void load();
  }, [load, props.initialClients]);

  const visible = props.hideTestTenants ? clients.filter((client) => !client.isTest) : clients;
  const hidden = clients.length - visible.length;
  // The roster obeys the SAME filter the client rows do: an admin who has just
  // hidden the dev seeds must not be offered one as a partner (the S2-02 law).
  const partnerOptions = props.hideTestTenants
    ? partners.filter((member) => !isTestTenant(member.displayName ?? "", member.email))
    : partners;
  const hiddenPartners = partners.length - partnerOptions.length;

  async function assign(client: UnassignedClient) {
    const partnerId = picked[client.clientId] ?? "";
    if (!partnerId) return;
    setBusy(client.clientId);
    setErrorKey((current) => ({ ...current, [client.clientId]: "" }));
    const response = await run(
      `/api/admin/clients/${encodeURIComponent(client.clientId)}/partner`,
      { method: "PATCH", body: { partnerId } },
      { refresh: false }
    );
    setBusy(null);
    if (!response.ok) {
      let code: string | null = null;
      try {
        const body = await response.json() as { error?: string };
        if (typeof body.error === "string") code = body.error;
      } catch {
        // The generic sentence covers an unreadable body.
      }
      setErrorKey((current) => ({ ...current, [client.clientId]: assignErrorKey(code) }));
      return;
    }
    // The row leaves the queue because the client left the queue - the list is
    // re-read rather than patched, so the count and the rows cannot disagree.
    await load();
  }

  if (!loaded || (visible.length === 0 && hidden === 0)) return null;

  return (
    // 🔴 NOT `.staff-today-stream`. That class carries the queue's own
    // internals - `> header` is a space-between flex row and `li` is a
    // `10px | 1fr | auto` grid built for a one-line queue row - so borrowing
    // the frame also borrowed a layout this section's markup is not, and the
    // name overlapped the facts under it. The frame is declared here instead,
    // to the same values, so the section reads as part of the day without
    // inheriting rules written for somebody else's row.
    <section className="staff-unassigned" data-today-stream="unassigned">
      <header className="staff-unassigned-head">
        <p className="panel-label">{props.t("staffConsole.unassigned.eyebrow")}</p>
        {/* One sentence, one population (the S3-02 law). The heading counts
            the rows the reader can SEE; the test-filter note below reconciles
            what the local filter took away, and the truncation line reconciles
            what the SERVER page left behind. A silent top-N would read as
            "that is everybody", which is the one thing this queue must never
            imply, so the short page says it is short. */}
        <h2>{`${props.t("staffConsole.unassigned.title")} (${visible.length})`}</h2>
        <p className="staff-unassigned-lead">{props.t("staffConsole.unassigned.lead")}</p>
        {clients.length < total && (
          <p className="staff-unassigned-truncated">
            {props.t("staffConsole.unassigned.truncated")
              .replace("{shown}", String(clients.length))
              .replace("{total}", String(total))}
          </p>
        )}
        <TestFilterNote
          hidden={hidden}
          shown={visible.length}
          onShowTest={props.onHideTestTenantsChange ? () => props.onHideTestTenantsChange?.(false) : undefined}
          t={props.t}
        />
      </header>
      {visible.length === 0 ? (
        <p className="staff-unassigned-empty">{props.t("staffConsole.unassigned.empty")}</p>
      ) : (
        <ol className="staff-unassigned-list">
          {visible.map((client) => {
            const productFallback = unassignedProductKey(client);
            const rowError = errorKey[client.clientId];
            return (
              <li key={client.clientId} className="staff-unassigned-row">
                <div className="staff-unassigned-identity">
                  <strong>{client.name}</strong>
                  <span className="staff-unassigned-email">{client.email}</span>
                </div>
                <dl className="staff-unassigned-facts">
                  <div>
                    <dt>{props.t("staffConsole.unassigned.product")}</dt>
                    <dd>{client.product ?? props.t(productFallback as string)}</dd>
                  </div>
                  <div>
                    <dt>{props.t("staffConsole.unassigned.market")}</dt>
                    <dd>{props.t(client.marketLanguage === "en" ? "common.languageEn" : "common.languageCs")}</dd>
                  </div>
                  <div>
                    <dt>{props.t("staffConsole.unassigned.arrived")}</dt>
                    <dd>
                      <time dateTime={client.createdAt}>
                        {new Intl.DateTimeFormat(intlLanguage(props.locale), {
                          day: "numeric", month: "numeric", year: "numeric", timeZone
                        }).format(new Date(client.createdAt))}
                      </time>
                    </dd>
                  </div>
                </dl>
                <p className="staff-unassigned-profile">
                  {client.profileFacts > 0
                    ? translateCount(props.locale, "staffConsole.unassigned.profileFacts", client.profileFacts)
                    : props.t("staffConsole.unassigned.profileEmpty")}
                  {" · "}
                  <button
                    className="quiet-link"
                    type="button"
                    onClick={() => props.onOpenClient(client.clientId, null)}
                  >
                    {props.t("staffConsole.unassigned.openClient")}
                  </button>
                </p>
                {partnerOptions.length === 0 ? (
                  <p className="staff-unassigned-no-partners">
                    {props.t(hiddenPartners > 0
                      ? "staffConsole.team.allFiltered"
                      : "staffConsole.unassigned.noPartners")}
                  </p>
                ) : (
                  <div className="staff-unassigned-assign">
                    <label
                      className="inline-select"
                      htmlFor={`unassigned-partner-${client.clientId}`}
                    >
                      <span>{props.t("staffConsole.unassigned.assignLabel")}</span>
                      <select
                        id={`unassigned-partner-${client.clientId}`}
                        value={picked[client.clientId] ?? ""}
                        disabled={busy === client.clientId}
                        onChange={(event) =>
                          setPicked((current) => ({ ...current, [client.clientId]: event.target.value }))}
                      >
                        <option value="">{props.t("staffConsole.team.choose")}</option>
                        {partnerOptions.map((member) => (
                          <option key={member.id} value={member.id}>
                            {`${member.displayName ?? member.email} · ${member.email}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      className="primary-button"
                      type="button"
                      disabled={!picked[client.clientId] || busy === client.clientId}
                      onClick={() => void assign(client)}
                    >
                      {props.t(busy === client.clientId
                        ? "staffConsole.unassigned.assigning"
                        : "staffConsole.unassigned.assignSubmit")}
                    </button>
                  </div>
                )}
                {rowError && <p className="staff-unassigned-error" role="alert">{props.t(rowError)}</p>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
